<template>
  <b-card class='my-2' header="Pack, Ship, & Track">
    <b-row>
      <b-col cols=12 lg=10 v-if="Object.keys(shipments).length === 0">
        No shipping/tracking info found!
      </b-col>
      <b-col cols=12 lg=10 v-else>
        <b-row
          v-for='(shipment, id) in shipments' :key='id'
          class='my-3 pb-3 align-items-center' style='border-bottom: thick solid #999;'
        >
          <b-col class='text-center' cols=12 sm=3>
            <div>
              <span v-b-popover.hover="carrierTooltip(shipment)">
                <i :class="carrierIcon(shipment)"></i>
              </span>
            </div>
            <div><b>{{shipment.shipmethod}}</b></div>
            <div>Shipped:<nice-date :value=shipment.shiptime is-utc /></div>
            <div v-if="shipment.deliverydate && shipment.deliverydate > '2000'">
              Delivery:<nice-date :value=shipment.deliverydate is-utc :showTime=false />
            </div>
            <div v-if="shipment.customer_fedex_account && shipment.customer_fedex_account !== '334941582'">
              <b>Shipped through a customer's FedEx account</b>
            </div>
            <div>
              <a target=_blank :href="getTrackingLink(shipment.mastertracknum || shipment.boxes[0].tracknum)">
                {{shipment.mastertracknum || shipment.boxes[0].tracknum}}
              </a>
            </div>
          </b-col>
          <b-col>
            <b-row v-for="(box,ix) in shipment.boxes" :key=ix class='my-2 align-items-center'>
              <b-col cols=2 class='d-none d-lg-flex'>
                <span class='fa-layers fa-fw fa-3x'>
                  <i v-if='shipment.ordernum === order.num' class='fas fa-box-open'></i>
                  <i v-else class='fas fa-barcode text-info' :title="`This package was shipped with a custom label - ${shipment.ordernum}`"></i>
                  <span class='fa-layers-counter' style='background:tomato;'>{{ix + 1}}</span>
                </span>
              </b-col>
              <b-col cols=6 md=3 lg=3>
                <div>
                  <a target=_blank :href="getTrackingLink(box.tracknum)">{{box.tracknum}}</a>
                </div>
              </b-col>
              <b-col cols=6 md=3 lg=2>
                <div>{{box.box_size}}</div>
                <div>{{box.andymark_weight}} lbs</div>
              </b-col>
              <b-col v-if="box.items.length > 0">
                <b-btn v-b-toggle="`collapse-${shipment.shipid}-${ix}`" variant=info size=sm block>
                  Show {{box.items.length}} {{box.items.length == 1 ? 'Item' : 'Items'}}
                </b-btn>
                <b-collapse :id="`collapse-${shipment.shipid}-${ix}`">
                  <b-table striped hover :items=box.items :fields=fields />
                </b-collapse>
              </b-col>
            </b-row>
          </b-col>
        </b-row>
        <b-row v-if=returnData>
          <b-col class='text-center' cols=3>
            <div><i class='fa-fw fa-3x fas fa-exchange-alt'></i></div>
            <div><b>Customer Return</b></div>
            <div>Shipped:<nice-date :value="returnData.shiptime" /></div>
            <div v-if="returnData.customer_fedex_account && returnData.customer_fedex_account !== '334941582'">
              <b>Shipped through a customer FedEx account</b>
            </div>
            <div><a target=_blank :href="getTrackingLink(returnData.tracknum)">
              {{returnData.tracknum}}
            </a></div>
          </b-col>
        </b-row>
      </b-col>
      <b-col>
        <b-btn @click=downloadDhlCsv variant=warning block class='mb-2'>Download DHL Customs Data</b-btn>
        <b-btn :to="{ name: 'PackAndShip2', query: { orderNumber: order.num } }" variant=primary block size=lg target=_blank>
          <i class='fas fa-box'></i>
          Open in Pack/Ship Estimator
        </b-btn><br/>
        <div v-if="order.shippings.length > 0 && complete">
          <b-row class='align-items-center'>
            <b-col><b>Customer Shipping Cost:</b></b-col>
            <b-col class='text-right'>{{order.shippingTotal | formatDollars}}</b-col>
          </b-row>
          <b-row class='align-items-center'>
            <b-col><b>Our Shipping Cost</b></b-col>
            <b-col class='text-right'>{{netCharge | formatDollars}}</b-col>
          </b-row>
          <b-row class='align-items-center'>
            <b-col><b>Shipping P/L</b></b-col>
            <b-col class='text-right'>{{order.shippingTotal - netCharge | formatDollars}}</b-col>
          </b-row>
        </div>
      </b-col>
    </b-row>
  </b-card>
</template>

<script>
import axios from 'axios'
import trackingUrl from 'tracking-url';
import NiceDate from '../../utility/NiceDate.vue'
import DataMerger from '../../utility/DataMerger.vue';
const BASE_API = process.env.VUE_API_ROOT

export default {
  props: {
    order: { type: Object, required: true },
  },
  components: {
    NiceDate,
    DataMerger,
  },
  computed: {
    complete() {
      return this.order.items.every(x => x.soItemStatus.id >= 50)
    },
    netCharge() {
      return Object.values(this.shipments).map(shipment => {
        return shipment.boxes.map(box => box.net_charge).reduce((x, memo) => memo + x, 0)
      }).reduce((x, memo) => memo + x, 0)
    }
  },
  data() {
    return {
      shipments: { },
      returnData: { },
      fields: [
        { key: 'quantity', label: 'Qty', class: 'w-10' },
        { key: 'sku', label: 'Product', class: 'w-15' },
        { key: 'description', label: 'Description', class: 'w-75' },
      ]
    };
  },
  mounted() {
    let soItemIds = [...new Set([].concat(...this.order.items.map(x => x.shippings.map(y => y.shipItemId))))]
    axios.all([
      axios.get(`${BASE_API}/am-old-db/shipping/${this.order.num}`),
      axios.get(`${BASE_API}/am-old-db/shipping/packing-info/${encodeURI(soItemIds)}`),
      axios.get(`${BASE_API}/am-old-db/shipping/return/${this.order.num}`)
    ]).then(axios.spread((shipmentData, boxData, returnData) => {
      shipmentData.data.forEach(s => {
        console.log(s)
        if (!this.shipments[s.shipid]) {
          this.$set(this.shipments, s.shipid, {
            gateway: s.gateway,
            shiptime: s.shiptime,
            deliverdate: s.deliverdate,
            shipmethod: (s.shipmethod || "").replace('&reg;', '®'),
            customer_fedex_account: s.customer_fedex_account,
            mastertracknum: s.mastertracknum
          })
          this.shipments[s.shipid].boxes = []
        }
        this.shipments[s.shipid].boxes.push({
          andymark_weight: s.andymark_weight,
          box_size: s.box_size,
          tracknum: s.tracknum,
          net_charge: s.netcharge,
          items: []
        })
      })
      boxData.data.forEach(b => {
        const soItem = this.order.items.filter(x => x.shippings.some(y => y.shipItemId === b.shipitemid))[0]
        if (soItem) {
          b.sku = soItem.sku
          b.description = soItem.description
          b.shipid = soItem.shippings.filter(x => x.shipItemId === b.shipitemid)[0].shipId
        } else {
          b.sku = '???'
          b.description = '???'
          b.shipid = null
        }
        if (!this.shipments[b.shipid].boxes[b.boxnum-1]) {
          this.shipments[b.shipid].boxes[b.boxnum-1].items = []
        }
        this.shipments[b.shipid].boxes[b.boxnum - 1].items.push(b)
      })
      this.returnData = returnData.data[0]
    })).catch(error => {
      console.error(error)
    })
  },
  methods: {
    downloadDhlCsv() {
      let r = undefined
      axios({
        url: `${BASE_API}/fishbowl/shipping/dhl-data.csv?order=${this.order.num}`,
        method: 'GET',
        responseType: 'blob',
      }).then(response => {
        r = response
      }).catch(error => {
        r = error.response
        alert('There is missing data in the downloaded CSV!\n\nPlease follow up/fix the mising data before using it.')
      }).finally(() => {
        let link = document.createElement('a')
        link.href = window.URL.createObjectURL(new Blob([r.data]))
        link.setAttribute('download', `${this.order.num}-data.csv`)

        document.body.appendChild(link)
        link.click()
      })
    },
    carrierIcon(shipping) {
      if (shipping.gateway == 'FEDEX') {
        return ['fa-3x', 'fab', 'fa-fedex']
      } else if (shipping.gateway == 'USPS') {
        return ['fa-3x', 'fab', 'fa-usps']
      } else if (shipping.gateway == 'OTHER') {
        if (shipping.shipmethod.match(/pick ?up/i)) {
          return ['fa-3x', 'fas', 'fa-dolly']
        } else {
          return ['fa-3x', 'fas', 'fa-truck']
        }
      } else if (shipping.gateway == null) {
        return ['fa-3x', 'fas', 'fa-dumpster-fire']
      } else {
        return ['fa-3x', 'fas', 'fa-exclamation-circle']
      }
    },
    carrierTooltip(shipping) {
      if (shipping.gateway == 'FEDEX') {
        return "FedEx"
      } else if (shipping.gateway == 'USPS') {
        return "USPS"
      } else if (shipping.gateway == 'OTHER') {
        if (shipping.shipmethod.match(/pick ?up/i)) {
          return "Pick Up"
        } else {
          return "Delivery"
        }
      } else if (shipping.gateway == null) {
        return "This wasn't shipped normally..."
      } else {
        return "Unknown Shipper"
      }
    },
    getTrackingLink(trackingNumber) {
      const tracker = trackingNumber ? trackingUrl(trackingNumber) : undefined
      return tracker ? tracker.url : ''
    }
  }
}
</script>



// WEBPACK FOOTER //
// src/components/sales-order/view-order-2/PackShip.vue